import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getLocalStorageItem, errorToast } from '../../../utils/helper';
import { CompanyApi } from '../../../api/companyApi';
import Topbar from '../../PulseSurvey/Topbar';
import Breadcrumb from '../../../component/common/Breadcrumb';
import Loader from '../../../component/common/Loader';
import InfoPopup from '../../../component/common/InfoPopup';
import AudioPlayer from '../../../component/AudioPlayer/AudioPlayer';

// Import recognition icons
import ThankYouIcon from './icons/High-Five--Streamline-Flex 1.svg';
import GreatWorkIcon from './icons/Trophy--Streamline-Flex 1.png';
import TeamworkIcon from './icons/User-Multiple-Group--Streamline-Flex 2.svg';
import LeadershipIcon from './icons/Location-Compass-2--Streamline-Flex 1.svg';
import InnovationIcon from './icons/Flash-1--Streamline-Flex 1.svg';
import WellbeingSupportIcon from './icons/Sprout--Streamline-Flex 1.svg';
import KindnessIcon from './icons/Happy-Face--Streamline-Flex 1.svg';
import ResilienceIcon from './icons/Shield-Heart-Care-Protection--Streamline-Flex 1.svg';
import ProblemSolvingIcon from './icons/Module-Puzzle-1--Streamline-Flex 1.svg';
import SteppingUpIcon from './icons/Rocket--Streamline-Flex 1.svg';
import EmotionalSupportIcon from './icons/Happy-Sad-Faces--Streamline-Flex 1.svg';
import ListeningIcon from './icons/Ear-Hearing--Streamline-Flex 1.svg';

/**
 * Shoutouts Main Component
 * Main page for Employee Recognition (Shoutouts)
 * @returns {JSX.Element} Shoutouts component
 */
const Shoutouts = () => {
  const navigate = useNavigate();
  const filterDropdownRef = useRef(null);
  const [activeTab, setActiveTab] = useState('all'); // all, received, sent
  const [shoutouts, setShoutouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filters, setFilters] = useState({
    visibility: '',
    behaviour: ''
  });
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);

  const userData = getLocalStorageItem('userData')
    ? JSON.parse(getLocalStorageItem('userData'))
    : null;
  const slug = userData?.slug || 'b2b';

  const pages = [
    {
      name: 'Employee Recognition',
      href: `/${slug}/shoutouts`,
      current: true
    }
  ];

  useEffect(() => {
    loadShoutouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pagination.currentPage, filters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  const loadShoutouts = async () => {
    setLoading(true);
    try {
      let response;
      const filterTypeMap = {
        all: 'All',
        received: 'Received',
        sent: 'Send'
      };

      const filterType = filterTypeMap[activeTab];
      response = await CompanyApi.getAllShoutouts(
        filterType,
        pagination.currentPage,
        pagination.limit,
        filters
      );

      if (response?.data?.meta?.code === 1) {
        const data = response.data.data || [];
        setShoutouts(data);

        // Update pagination info
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.totalPages || 1,
          totalRecords: response.data.totalRecords || 0
        }));
      } else {
        setShoutouts([]);
        if (response?.data?.meta?.message) {
          errorToast(response.data.meta.message);
        }
      }
    } catch (error) {
      console.error('Error loading shoutouts:', error);
      errorToast('Failed to load recognitions. Please try again.');
      setShoutouts([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleSendRecognition = () => {
    navigate(`/${slug}/shoutouts/send`);
  };

  const fetchInfo = async () => {
    // Placeholder for info content - can be implemented later
    setInfo({
      description:
        "Employee Recognition allows you to acknowledge and celebrate your team's contributions. Send recognitions for various behaviors like teamwork, innovation, leadership, and more. Track all recognitions sent and received across your organization."
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const resetFilters = () => {
    setFilters({ visibility: '', behaviour: '' });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const behaviourTypes = [
    { label: 'All Types', value: '' },
    { label: 'Thank You', value: 'THANK_YOU' },
    { label: 'Great Work', value: 'GREAT_WORK' },
    { label: 'Teamwork', value: 'TEAMWORK' },
    { label: 'Leadership', value: 'LEADERSHIP' },
    { label: 'Innovation', value: 'INNOVATION' },
    { label: 'Wellbeing Support', value: 'WELLBEING_SUPPORT' },
    { label: 'Kindness', value: 'KINDNESS' },
    { label: 'Resilience', value: 'RESILIENCE' },
    { label: 'Problem Solving', value: 'PROBLEM_SOLVING' },
    { label: 'Stepping Up', value: 'STEPPING_UP' },
    { label: 'Emotional Support', value: 'EMOTIONAL_SUPPORT' },
    { label: 'Listening', value: 'LISTENING' }
  ];

  const hasActiveFilters = filters.visibility || filters.behaviour;

  return (
    <>
      <div className="relative lg:mb-[45px]">
        {loading && <Loader />}
        <Helmet>
          <meta charSet="utf-8" />
          <title>Employee Recognition | Shoorah Admin</title>
        </Helmet>
        <Topbar
          title={'Employee Recognition'}
          infoBtn={true}
          setOpen={setOpen}
          fetchInfo={fetchInfo}
        />
        <Breadcrumb pageList={pages} className="lg:mt-8" />

        <div className="mt-6">
          {/* Action Buttons */}
          <div className="flex justify-end items-center mb-6 space-x-3">
            {/* Filter Button */}
            <div className="relative" ref={filterDropdownRef}>
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className={`px-4 py-2.5 border rounded-lg flex items-center space-x-2 transition-colors ${
                  hasActiveFilters
                    ? 'border-shoorah-primary text-shoorah-primary bg-shoorah-primary/5'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                <span className="text-sm font-medium">Filter</span>
                {hasActiveFilters && (
                  <span className="ml-1 px-2 py-0.5 bg-shoorah-primary text-white text-xs rounded-full">
                    {(filters.visibility ? 1 : 0) + (filters.behaviour ? 1 : 0)}
                  </span>
                )}
              </button>

              {/* Filter Dropdown */}
              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-shoorah-darkBorder z-50">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Filter Recognitions
                      </h3>
                      <button
                        onClick={() => setShowFilterDropdown(false)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Visibility Filter */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Visibility
                      </label>
                      <select
                        value={filters.visibility}
                        onChange={(e) => handleFilterChange('visibility', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-shoorah-primary focus:border-transparent"
                      >
                        <option value="">All</option>
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                      </select>
                    </div>

                    {/* Behaviour Type Filter */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Recognition Type
                      </label>
                      <select
                        value={filters.behaviour}
                        onChange={(e) => handleFilterChange('behaviour', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-shoorah-primary focus:border-transparent"
                      >
                        {behaviourTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Filter Actions */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-shoorah-darkBorder">
                      <button
                        onClick={resetFilters}
                        disabled={!hasActiveFilters}
                        className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Clear All
                      </button>
                      <button
                        onClick={() => setShowFilterDropdown(false)}
                        className="btn-primary"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleSendRecognition}
              className="btn-primary"
            >
              Send Recognition
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-shoorah-darkBorder mb-6">
            <div className="flex space-x-8">
              <button
                onClick={() => {
                  setActiveTab('all');
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'all'
                    ? 'border-shoorah-primary text-shoorah-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                All Recognitions
              </button>
              <button
                onClick={() => {
                  setActiveTab('received');
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'received'
                    ? 'border-shoorah-primary text-shoorah-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Received
              </button>
              <button
                onClick={() => {
                  setActiveTab('sent');
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'sent'
                    ? 'border-shoorah-primary text-shoorah-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Sent
              </button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shoorah-primary"></div>
            </div>
          ) : shoutouts.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                No recognitions yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {activeTab === 'all'
                  ? 'No recognitions have been sent in your organization yet.'
                  : activeTab === 'received'
                  ? "You haven't received any recognitions yet."
                  : "You haven't sent any recognitions yet."}
              </p>
              {activeTab !== 'received' && (
                <button
                  onClick={handleSendRecognition}
                  className="btn-primary"
                >
                  Send First Recognition
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 mb-6">
                {shoutouts.map((shoutout) => (
                  <ShoutoutCard key={shoutout._id} shoutout={shoutout} />
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    Previous
                  </button>

                  <div className="flex items-center space-x-2">
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        // Show first page, last page, current page, and pages around current
                        return (
                          page === 1 ||
                          page === pagination.totalPages ||
                          Math.abs(page - pagination.currentPage) <= 1
                        );
                      })
                      .map((page, index, array) => {
                        // Add ellipsis if there's a gap
                        const prevPage = array[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;

                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && <span className="px-2 text-gray-500">...</span>}
                            <button
                              onClick={() => handlePageChange(page)}
                              className={`btn-sm ${pagination.currentPage === page ? 'btn-primary' : 'btn-secondary'}`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>

                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <InfoPopup
        open={open}
        data={info}
        title="Employee Recognition"
        handleClose={() => setOpen(false)}
      />
    </>
  );
};

// ShoutoutCard Component
const ShoutoutCard = ({ shoutout }) => {
  const [audioPlaying, setAudioPlaying] = useState(false);

  const getBehaviourLabel = (behaviour) => {
    const labels = {
      THANK_YOU: {
        label: 'Thank You',
        icon: ThankYouIcon,
        color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      },
      GREAT_WORK: {
        label: 'Great Work',
        icon: GreatWorkIcon,
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      TEAMWORK: {
        label: 'Teamwork',
        icon: TeamworkIcon,
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      LEADERSHIP: {
        label: 'Leadership',
        icon: LeadershipIcon,
        color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      },
      INNOVATION: {
        label: 'Innovation',
        icon: InnovationIcon,
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      },
      WELLBEING_SUPPORT: {
        label: 'Wellbeing Support',
        icon: WellbeingSupportIcon,
        color: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
      },
      KINDNESS: {
        label: 'Kindness',
        icon: KindnessIcon,
        color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      },
      RESILIENCE: {
        label: 'Resilience',
        icon: ResilienceIcon,
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      },
      PROBLEM_SOLVING: {
        label: 'Problem Solving',
        icon: ProblemSolvingIcon,
        color: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
      },
      STEPPING_UP: {
        label: 'Stepping Up',
        icon: SteppingUpIcon,
        color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200'
      },
      EMOTIONAL_SUPPORT: {
        label: 'Emotional Support',
        icon: EmotionalSupportIcon,
        color: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200'
      },
      LISTENING: {
        label: 'Listening',
        icon: ListeningIcon,
        color: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200'
      }
    };

    return (
      labels[behaviour] || {
        label: behaviour,
        icon: GreatWorkIcon,
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      }
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const behaviourInfo = getBehaviourLabel(shoutout.behaviour);

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-shoorah-darkBorder shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-shoorah-primary/10 rounded-full flex items-center justify-center">
            <span className="text-shoorah-primary font-semibold">
              {shoutout.sender?.name?.charAt(0).toUpperCase() || 'A'}
            </span>
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {shoutout.sender?.name || 'Anonymous'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(shoutout.createdAt)}
            </p>
          </div>
        </div>

        <span
          className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${behaviourInfo.color}`}
        >
          <img src={behaviourInfo.icon} alt={behaviourInfo.label} className="w-4 h-4" />
          <span>{behaviourInfo.label}</span>
        </span>
      </div>

      {/* Recipient */}
      <div className="mb-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Recognized{' '}
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {shoutout.recipient?.name || shoutout.department || 'Team'}
          </span>
        </p>
      </div>

      {/* Message */}
      {shoutout.message && (
        <div className="mb-4">
          <p className="text-gray-700 dark:text-gray-300 italic">&quot;{shoutout.message}&quot;</p>
        </div>
      )}

      {/* Image */}
      {shoutout.image && (
        <div className="mb-4">
          <img
            src={shoutout.image}
            alt="Shoutout attachment"
            className="h-32 w-auto object-cover rounded-lg border border-gray-200 dark:border-shoorah-darkBorder"
          />
        </div>
      )}

      {/* Audio */}
      {shoutout.audio && (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-shoorah-darkBorder">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
              />
            </svg>
            Audio Message
          </p>
          <AudioPlayer
            audioPath={shoutout.audio}
            playing={audioPlaying}
            onPlay={() => setAudioPlaying(true)}
            pauseHandler={() => setAudioPlaying(false)}
          />
        </div>
      )}

      {/* Footer - Visibility badge */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-shoorah-darkBorder">
        <div className="flex items-center space-x-2">
          {shoutout.isPrivate ? (
            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              Private
            </span>
          ) : (
            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Public
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shoutouts;
